import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import WebApp from '@twa-dev/sdk';

import { buyMarketPrize, getMarketPrize } from '@/shared/api/market';
import { type MarketPrize } from '@/shared/api/market/types';
import { ErrorLogger } from '@/shared/service/logger/ErrorLogger';
import { useAppActions } from '@/shared/stores/app-store/app-store';

import { NO_AD_SHOP_ITEM_ID } from '../constants';

export type NoAdPurchaseStatus = 'loading' | 'ready' | 'error';

interface UseNoAdPurchase {
  status: NoAdPurchaseStatus;
  prize: MarketPrize | null;
  /** Идёт fetch инвойса или открыт телеграм-popup оплаты. */
  isPurchasing: boolean;
  /** Платёж в процессе на беке (inProcess из ответа маркета). */
  isInProcess: boolean;
  purchase: () => void;
}

export const useNoAdPurchase = (): UseNoAdPurchase => {
  const [status, setStatus] = useState<NoAdPurchaseStatus>('loading');
  const [prize, setPrize] = useState<MarketPrize | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const cancelledRef = useRef(false);
  const { fetchUserProfile, markNoAdPurchased } = useAppActions();

  useEffect(() => {
    cancelledRef.current = false;
    getMarketPrize(NO_AD_SHOP_ITEM_ID)
      .then((data) => {
        if (cancelledRef.current) return;
        setPrize(data);
        setStatus('ready');
      })
      .catch((error) => {
        if (cancelledRef.current) return;
        ErrorLogger.error(error as Error, 'getMarketPrize(noAd) failed');
        setStatus('error');
      });

    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const purchase = useCallback(async () => {
    if (!prize || isPurchasing) return;

    const cancelPurchaseTimeout = setTimeout(() => {
      setIsPurchasing(false);
    }, 3000);

    setIsPurchasing(true);
    try {
      const { url } = await buyMarketPrize({
        id: prize.id,
        price: prize.price.value,
        currency: prize.price.currency,
        data: {},
      });

      if (!url) {
        ErrorLogger.info('buyMarketPrize returned no invoice url');
        setIsPurchasing(false);
        return;
      }

      WebApp.openInvoice(url, (invoiceStatus) => {
        clearTimeout(cancelPurchaseTimeout);
        setIsPurchasing(false);
        if (invoiceStatus === 'paid') {
          markNoAdPurchased();
          void fetchUserProfile();
        }
      });
    } catch (error) {
      ErrorLogger.error(error as Error, 'buyMarketPrize(noAd) failed');
      setIsPurchasing(false);
    }
  }, [prize, isPurchasing, fetchUserProfile, markNoAdPurchased]);

  return {
    status,
    prize,
    isPurchasing,
    isInProcess: prize?.inProcess === true,
    purchase,
  };
};
